import { deleteWorkspaceMembership } from '@modules/memberships/memberships'
import { useProjectsStore } from '@modules/projects/stores/projects.store'
import { resetProjectsFetchInFlight } from '@modules/projects/stores/projects.thunks'

import { clearClientStoresBeforeWorkspaceContextChange } from '../utils/clearClientStoresBeforeWorkspaceContextChange'
import type { Workspace } from '../workspaces.type'

import { useWorkspacesStore } from './workspaces.store'
import { getCurrentWorkspace, getWorkspacesByUserId } from './workspaces.thunks'

export type ReloadWorkspaceContextOptions = {
  fallbackWorkspaceId?: string | null
  preferredWorkspaceId?: string | null
}

export async function leaveWorkspaceMembershipAndReloadClientData(workspaceId: string, userId: string): Promise<void> {
  await deleteWorkspaceMembership(workspaceId, userId)
  await reloadClientDataAfterWorkspaceMembershipChange()
}

export async function reloadClientDataAfterWorkspaceMembershipChange(
  options?: ReloadWorkspaceContextOptions
): Promise<void> {
  clearClientStoresBeforeWorkspaceContextChange()
  useProjectsStore.getState().invalidateProjectsCache()
  resetProjectsFetchInFlight()

  await getWorkspacesByUserId({ force: true })

  const { workspaces } = useWorkspacesStore.getState()
  const preferredId = options?.preferredWorkspaceId
  const fallbackId = options?.fallbackWorkspaceId

  let target: Workspace | null = null
  if (preferredId) {
    target = workspaces.find((workspace) => workspace.workspaceId === preferredId) ?? null
  }
  if (!target && fallbackId) {
    target = workspaces.find((workspace) => workspace.workspaceId === fallbackId) ?? null
  }
  if (!target) {
    target = getCurrentWorkspace()
  }
  if (!target && workspaces.length > 0) {
    target = workspaces[0] ?? null
  }

  if (target) {
    const { switchWorkspace } = await import('@modules/projects/utils/switchWorkspace')
    await switchWorkspace(target)
    return
  }

  useWorkspacesStore.getState().setState({ currentWorkspace: null })
}
